/**
 * Browser fallback for badge printing, meant for testing the badge without
 * the stations app. Launching the kiosk with `?browser_print=1` turns it on
 * for the current tab; `?browser_print=0` turns it off again. In the stations
 * app the native bridge is always used instead.
 */

export const BROWSER_PRINT_PARAM = "browser_print";
const STORAGE_KEY = "tactic_kiosk_browser_print";

export const enableBrowserBadgePrintFromUrl = (
  searchParams: Pick<URLSearchParams, "get">
): void => {
  if (typeof window === "undefined") return;
  const value = searchParams.get(BROWSER_PRINT_PARAM);
  if (value === null) return;
  try {
    if (value === "0" || value === "false") {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, "1");
    }
  } catch {
    // Storage can be unavailable (private mode); the flag simply does not stick.
  }
};

export const isBrowserBadgePrintEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

interface BrowserBadgePrintOptions {
  widthMm: number;
  heightMm: number;
}

/** Print an already rendered badge PNG through the browser print dialog at its physical size. */
export const printBadgeInBrowser = (
  imageDataUrl: string,
  { widthMm, heightMm }: BrowserBadgePrintOptions
): void => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentWindow?.document;
  if (!iframeDoc) {
    document.body.removeChild(iframe);
    return;
  }

  iframeDoc.open();
  iframeDoc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Visitor Badge</title>
        <style>
          @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
          html, body { margin: 0; padding: 0; }
          img { display: block; width: ${widthMm}mm; height: ${heightMm}mm; }
        </style>
      </head>
      <body><img src="${imageDataUrl}" alt="" /></body>
    </html>
  `);
  iframeDoc.close();

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.print();
      // Remove iframe after a delay to allow print dialog to complete
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 100);
  };
};
